import React from 'react';
import * as mobxReact from 'mobx-react';
import { IntlShape } from 'react-intl';
import { IAttendeeDetailsDTO, IAttendeeOrganizationDTO } from '../../../api/attendees.dao';
import Text from '../text/text';
import Bell from '../../../images/svg/bell';
import { OrganizationMultiSelectButton } from '../multi-select/organization-multi-select-button';
import { MultiSelectFilterType } from '../multi-select/make-multi-select-in-popover';
import { CollapsedOrganizations } from './collapsed-organizations';
import './attendee-column.scss';
import { AttendeeInfo } from './_attendee-info/_attendee-info';
import { OpenFrom } from '../../../tracking-service/tracking-values';
import { addText, cancelText } from '../../../constants/common-labels.constants';
import { AttendeeColumnPopoverViewmodel } from './attendee-column-popover/attendee-column-popover.viewmodel';

interface IProps {
    organizations: IAttendeeOrganizationDTO[];
    withAttendeeStatus?: boolean;
    isCollapsed?: boolean;
    searchValue?: string;
    eventId?: number;
    accountId?: number;
    withAttendeePopover?: boolean;
    withLiveStatus?: boolean;
    handleRemoveAttendeeClick?: (attendeeId: number, organizationId: number) => void;
    withRemove?: boolean;
    withAddButton?: boolean;
    addButtonText?: string;
    handleAttendeesChange?: (attendees: IAttendeeOrganizationDTO[]) => void;
    intl?: IntlShape;
    allAttendees?: IAttendeeOrganizationDTO[];
    disabled?: Record<number, Record<number, boolean>>;
    createAttendeeFromForm?: () => void;
    fetchAttendeeList?: () => void;
    isMeetingUnderway?: boolean;
    loadingAttendees?: boolean;
    resetLoadingState?: () => void;
    attendeeDetailsPopoverVM?: AttendeeColumnPopoverViewmodel;
    additionalAttendeeId?: string;
    onAttendeeEditSaveCallback?: (attendee: IAttendeeDetailsDTO) => void;
    openFrom?: OpenFrom;
}

const AttendeeColumn = mobxReact.observer((props: IProps) => {
    const {
        organizations,
        isCollapsed,
        searchValue,
        withAddButton,
        addButtonText,
        handleAttendeesChange,
        intl,
        allAttendees,
        disabled,
        createAttendeeFromForm,
        fetchAttendeeList,
        loadingAttendees,
        resetLoadingState,
        ...otherProps
    } = props;

    const sortedOrganizations = organizations.slice().sort((a, b) => a.name.localeCompare(b.name));

    if (isCollapsed)
        return (<div className='attendee-column'>
            <CollapsedOrganizations
                organizations={sortedOrganizations}
                searchValue={searchValue}
                showBellIcon={sortedOrganizations.some(org => org.firstMeetingInTheDay)}
            />
        </div>);

    return (
        <div className='attendee-column'>
            {
                sortedOrganizations
                    .map(({ attendees = [], organizationId, name , firstMeetingInTheDay}) => {
                        return (
                            <div
                                className='attendee-list-item'
                                key={organizationId}
                            >
                                <div className='attendee-list-item__header-wrapper'>
                                    <Text
                                        tag='div'
                                        className='attendee-list-item__header'
                                        message={name}
                                        highlightValue={props.searchValue}
                                        withEllipsis
                                        withHighlight
                                    />
                                    {firstMeetingInTheDay && <div className='attendee-list-item__header-bell'><Bell/></div>}
                                </div>
                                {attendees
                                    .slice()
                                    .sort((a, b) => (a.lastName || '').localeCompare(b.lastName))
                                    .map((attendee => (
                                        <AttendeeInfo
                                            key={attendee.attendeeId}
                                            attendee={attendee}
                                            searchValue={searchValue}
                                            organizationId={organizationId}
                                            {...otherProps}
                                        />)
                                    )
                                )}
                            </div>
                        );
                    }
                    )
            }
            {withAddButton && <OrganizationMultiSelectButton
                    className='add-attendee-btn visible-while-cell-hovered'
                    keyProperty='organizationId'
                    nameProperty='name'
                    items={allAttendees}
                    value={organizations}
                    disabled={disabled}
                    text={addButtonText}
                    filterType={MultiSelectFilterType.Group}
                    onChange={handleAttendeesChange}
                    ActionButton={createAttendeeFromForm}
                    onClick={fetchAttendeeList}
                    applyButtonText={addText()}
                    resetButtonText={cancelText()}
                    searchPlaceholder={
                        intl.formatMessage(
                            {
                                id: 'oe.organization.searchOrganization',
                                description: 'Organization search input placeholder',
                                defaultMessage: 'Search',
                            }
                        )
                    }
                    position={'bottom'}
                    align={'center'}
                    loadingAttendees={loadingAttendees}
                    resetLoadingState={resetLoadingState}
                />}
        </div>
    );
});

export { AttendeeColumn };
